import { webCoreConfig } from "../config.js";
export class ApiError extends Error {
    status;
    code;
    slug;
    next;
    /**
     * WHICH FIELD THE SERVER BLAMED, when it named one (`apps/api/src/errors.ts` publishes
     * `error.field` on every validation failure). Carried here so a form can put the message
     * under the input that caused it instead of in a banner above the whole form — the one
     * difference between an error a person can fix and an error they have to guess at.
     */
    field;
    /** Whatever else the envelope carried (`error.details`), unshaped. */
    details;
    /**
     * The request id the API stamped on this failure (`request_id` in the envelope, else the
     * `X-Request-Id` header). The dashboard prints it next to the slug so a person can quote it
     * to support without opening the developer tools.
     */
    requestId;
    constructor(status, body, requestId = null) {
        const envelope = body?.error;
        super(envelope?.message ?? `Request failed with status ${status}`);
        this.name = "ApiError";
        this.status = status;
        this.code = envelope?.code === undefined ? undefined : String(envelope.code);
        this.slug = envelope?.slug;
        this.next = envelope?.next;
        this.field = typeof envelope?.field === "string" ? envelope.field : null;
        this.details = envelope?.details;
        const stamped = body?.request_id;
        this.requestId = typeof stamped === "string" ? stamped : requestId;
    }
}
export function apiBaseUrl() {
    return webCoreConfig().apiBaseUrl;
}
/**
 * The session's CSRF token, held in memory for the lifetime of the page.
 *
 * DELIBERATELY NOT PERSISTED. The session itself lives in an httpOnly cookie the page cannot
 * read; this value is handed back by `GET /v1/session` on every load, so there is nothing to
 * keep in `localStorage` and nothing for an XSS to find lying about between visits.
 */
let csrfToken = null;
export function setCsrfToken(value) {
    csrfToken = value;
}
export function currentCsrfToken() {
    return csrfToken;
}
export const defaultChannel = {
    baseUrl: () => webCoreConfig().apiBaseUrl,
    csrfToken: () => csrfToken,
    setCsrfToken: (value) => {
        csrfToken = value;
    },
};
/** A channel to another API host; its CSRF token lives in this closure only. */
export function createApiChannel(baseUrl) {
    let token = null;
    return {
        baseUrl,
        csrfToken: () => token,
        setCsrfToken: (value) => {
            token = value;
        },
    };
}
const UNSAFE = new Set(["POST", "PATCH", "DELETE", "PUT"]);
/**
 * The cabinet's locale, sent as `X-Locale` on every write so the mails a write triggers (a step-up
 * code, a team invite) come in the language the person reads the dashboard in. Writes only: a GET
 * stays a simple request without a CORS preflight.
 */
let requestLocale = null;
export function setRequestLocale(value) {
    requestLocale = value;
}
/**
 * An approved step-up id for the next write (`X-Step-Up`, auth-oauth-bot-spec.md §1.4). Set only
 * for the synchronous start of `withStepUpHeader`'s call: every client function reaches `send`
 * before its first `await`, so the header lands on that call's request and on nothing else.
 */
let stepUpForNextWrite = null;
/**
 * Run `call` with `X-Step-Up: <id>` on the write it makes. The repeat must be the identical
 * request the server refused with `1020`, so the caller passes the same closure again.
 */
export function withStepUpHeader(stepUpId, call) {
    stepUpForNextWrite = stepUpId;
    try {
        return call();
    }
    finally {
        stepUpForNextWrite = null;
    }
}
/**
 * Take the step-up id `withStepUpHeader` set, for a client function that must `await` something before its request
 * (the mainnet writes restore their CSRF token first): read synchronously, at the call's start, and passed back as
 * `options.stepUp`. Read after an await it is already gone, and the repeat goes out unconfirmed.
 */
export function takeStepUpForNextWrite() {
    const id = stepUpForNextWrite;
    stepUpForNextWrite = null;
    return id;
}
function buildUrl(base, path, query) {
    const url = new URL(`${base.replace(/\/$/, "")}${path}`);
    for (const [key, value] of Object.entries(query ?? {})) {
        if (value === undefined)
            continue;
        if (Array.isArray(value)) {
            for (const item of value)
                url.searchParams.append(key, String(item));
        }
        else {
            url.searchParams.set(key, String(value));
        }
    }
    return url.toString();
}
export async function request(path, options = {}) {
    const response = await send(path, options, "application/json");
    const text = await response.text();
    const parsed = text ? JSON.parse(text) : null;
    if (!response.ok) {
        throw new ApiError(response.status, parsed, response.headers.get("x-request-id"));
    }
    return parsed;
}
/**
 * The same call for a non-JSON answer (`GET /orders?format=csv`). A failure still arrives as
 * the JSON error envelope, so it is still an `ApiError`.
 */
export async function requestText(path, options) {
    const response = await send(path, options, options.accept);
    const text = await response.text();
    if (!response.ok) {
        let parsed = null;
        try {
            parsed = text ? JSON.parse(text) : null;
        }
        catch {
            parsed = null;
        }
        throw new ApiError(response.status, parsed, response.headers.get("x-request-id"));
    }
    return text;
}
async function send(path, options, accept) {
    const method = options.method ?? "GET";
    const channel = options.channel ?? defaultChannel;
    const headers = { Accept: accept, ...options.headers };
    if (options.body !== undefined)
        headers["Content-Type"] = "application/json";
    // A cookie travels on its own, so every write carries the token the session handed us.
    const token = channel.csrfToken();
    if (token !== null && UNSAFE.has(method))
        headers["X-CSRF-Token"] = token;
    if (requestLocale !== null && UNSAFE.has(method))
        headers["X-Locale"] = requestLocale;
    if (options.stepUp !== undefined && UNSAFE.has(method)) {
        headers["X-Step-Up"] = options.stepUp;
    }
    else if (stepUpForNextWrite !== null && UNSAFE.has(method)) {
        headers["X-Step-Up"] = stepUpForNextWrite;
        stepUpForNextWrite = null;
    }
    const response = await fetch(buildUrl(channel.baseUrl(), path, options.query), {
        method,
        headers,
        credentials: "include",
        ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
        ...(options.signal ? { signal: options.signal } : {}),
    });
    return response;
}
/** The endpoints the public site calls. All anonymous; none of them carries a key. */
export const api = {
    /** The service fee tiers (`GET /v1/fees`): bps by 30-day spend; the first tier is what a new buyer pays. */
    getFees: (signal) => request("/fees", signal ? { signal } : {}),
    /** Live inputs of a JustLend renter's own cost (`/tools/justlend-rental-calculator`). */
    getMarketJustLend: (signal) => request("/market/justlend", signal ? { signal } : {}),
    getPrices: (resource, signal) => request("/prices", {
        query: resource ? { resource } : {},
        ...(signal ? { signal } : {}),
    }),
    /**
     * The ask ladder for one resource and tier. Anonymous and cached for a few seconds, so
     * the widget may re-read it while the buyer moves the amount.
     */
    getOrderBook: (input = {}, signal) => request("/orderbook", {
        query: {
            ...(input.resource ? { resource: input.resource } : {}),
            ...(input.tier ? { tier: input.tier } : {}),
            ...(input.amount ? { amount: input.amount } : {}),
        },
        ...(signal ? { signal } : {}),
    }),
    /**
     * What the chain says about an address. Public since 2026-09-25 (owner decision; an anonymous
     * caller gets `our_active_orders: []`). A deployment that still refuses it is handled by the
     * callers: the widget asks the buyer instead (`order/address-check.ts`).
     */
    getAddressResources: (address, signal) => request(`/resources/${encodeURIComponent(address)}`, {
        ...(signal ? { signal } : {}),
    }),
    /**
     * The address's own transactions over the last 7 or 30 UTC days, from TronGrid through our API (public; fresh
     * reads are capped per server, so a burst can answer 429 `rate_limited`).
     */
    getAddressUsage: (address, days, signal) => request(`/resources/${encodeURIComponent(address)}/usage`, {
        query: { days: String(days) },
        ...(signal ? { signal } : {}),
    }),
    estimate: (input) => request("/estimate", {
        query: {
            resource: input.resource,
            amount: input.amount,
            tier: input.tier,
            ...(input.receiver ? { receiver: input.receiver } : {}),
        },
    }),
    createQuote: (body) => request("/quotes", { method: "POST", body }),
    /** An unsigned transfer to the published address with its receiver bound; sign it and broadcast it unchanged. */
    createWalletPayment: (body) => request("/wallet-payments", { method: "POST", body }),
    createOrder: (body) => request("/orders", { method: "POST", body }),
    getOrder: (id) => request(`/orders/${encodeURIComponent(id)}`),
};
//# sourceMappingURL=client.js.map